/* Manual trace edits on top of the automatic layout.
   An edit replaces a trace's interior bend points, and for word traces can also
   slide either end around its ring; dots or symbols re-flow along the new path.
   Link ends stay on their bars. Word-trace bends are stored relative to the word
   origin and link bends relative to the link's start bar, so edits follow their
   glyphs when other words change. */

import { KohdLayout, LinkShape, placeDots, placeItems, Pt, RingEnd, TraceShape } from './layout';
import { GapItem } from './parse';

export interface TraceEdit {
  /** Interior bend points, relative to the anchor. */
  bends: Pt[];
  /** Angle in radians (0 = east, clockwise) of a word trace's start / end on its ring. */
  start?: number;
  end?: number;
}

export type KohdEdits = Record<string, TraceEdit>;

const offset = (p: Pt, o: Pt): Pt => ({ x: p.x + o.x, y: p.y + o.y });

export const traceAnchor = (layout: KohdLayout, key: string): Pt | null => {
  for (const row of layout.rows) {
    for (const w of row.words) if (w.traces.some((t) => t.key === key)) return w.origin;
    for (const l of row.links) if (l.key === key) return l.start;
  }
  return null;
};

const onRing = (ring: RingEnd, angle: number): Pt => ({ x: ring.x + ring.r * Math.cos(angle), y: ring.y + ring.r * Math.sin(angle) });

function editTrace(t: TraceShape, edit: TraceEdit, origin: Pt): TraceShape {
  const first = edit.start === undefined ? t.points[0] : onRing(t.from, edit.start);
  const last = edit.end === undefined ? t.points[t.points.length - 1] : onRing(t.to, edit.end);
  const points = [first, ...edit.bends.map((b) => offset(b, origin)), last];
  return { ...t, points, dots: placeDots(points, t.groups) };
}

function editLink(l: LinkShape, { bends }: TraceEdit): LinkShape {
  const points = [l.points[0], ...bends.map((b) => offset(b, l.start)), l.points[l.points.length - 1]];
  const items: GapItem[] = l.items.map(({ s, at, angle, half, ...item }) => item as GapItem);
  return { ...l, points, items: placeItems(points, items) };
}

export function applyEdits(layout: KohdLayout, edits: KohdEdits): KohdLayout {
  if (Object.keys(edits).length === 0) return layout;
  return {
    ...layout,
    rows: layout.rows.map((row) => ({
      ...row,
      words: row.words.map((w) => ({
        ...w,
        traces: w.traces.map((t) => (edits[t.key] ? editTrace(t, edits[t.key], w.origin) : t)),
      })),
      links: row.links.map((l) => (edits[l.key] ? editLink(l, edits[l.key]) : l)),
    })),
  };
}

/** Interior bends of a trace as stored in edits (relative to its anchor). */
export function bendsOf(points: Pt[], anchor: Pt): Pt[] {
  return points.slice(1, -1).map((p) => ({ x: p.x - anchor.x, y: p.y - anchor.y }));
}
